// components/StartGameButton.jsx: M3-11 CTA. Enabled when:
//   user is host AND (all 4 seats filled OR bot_fill is ON)
//   AND room phase === 'waiting'.
// On click, sends START_GAME. Real server (M3-13) responds with
// GAME_STARTING containing bot_seat_assignments; the stub flips phase
// to 'starting' immediately.

import { useMemo } from 'preact/hooks';
import { evaluateStartGame, notReadyNote } from './start-game-logic.js';

/**
 * @param {{
 *   isHost: boolean,
 *   phase: 'waiting'|'starting'|'in_game',
 *   occupantCount: number,
 *   botFill: boolean,
 *   allReady?: boolean,
 *   notReadyCount?: number,
 *   onStart: () => void,
 * }} props
 */
export function StartGameButton({ isHost, phase, occupantCount, botFill, allReady = false, notReadyCount = 0, onStart }) {
  const { enabled, reason } = useMemo(
    () => evaluateStartGame({ isHost, phase, occupantCount, botFill }),
    [isHost, phase, occupantCount, botFill],
  );

  // Non-hosts simply don't see the button (the host owns this CTA).
  if (!isHost) return null;

  // B3b F3: a plain live note beside Start Game while anyone is not
  // ready. It updates with the occupant list; empty when all ready.
  const readyNote = notReadyNote(notReadyCount);

  // M3-14 AC #2: when every occupant is ready, surface a CSS class
  // change on the button to draw the host's attention. No animation
  // (M5 adds juice); we just add a class hook that the stylesheet
  // turns into a contrast-ring highlight.
  const cls = enabled && allReady ? 'start-game-button all-ready' : 'start-game-button';

  return (
    <div class="start-game-wrap" data-testid="start-game-wrap">
      <button
        type="button"
        class={cls}
        data-testid="start-game"
        data-all-ready={allReady ? 'true' : 'false'}
        onClick={enabled ? onStart : undefined}
        disabled={!enabled}
        aria-disabled={!enabled}
        aria-describedby={!enabled && reason ? 'start-game-reason' : undefined}
      >
        Start Game
      </button>
      {readyNote && (
        <p class="start-game-not-ready" data-testid="start-game-not-ready">
          {readyNote}
        </p>
      )}
      {!enabled && reason && (
        <p class="start-game-reason" id="start-game-reason" data-testid="start-game-reason">
          {reason}
        </p>
      )}
    </div>
  );
}
